import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check } from 'lucide-react';
import PageShell from '../components/PageShell';
import PillFilter from '../components/PillFilter';
import SegmentedToggle from '../components/SegmentedToggle';
import { TOPICS } from '../data/onboarding';
import { useProgress } from '../data/progress';
import { guidesFor, playTopic } from './WelcomePage';
import { BackHome } from './WelcomeNewsPage';
import './WelcomePage.css';

/* Guides (refonte du 2026-10-01, prototype de Codex) : une carte par marche à
   suivre, deux de front, filtrées par domaine. Chaque carte dit son domaine,
   son titre, sa phrase, et « Lire le guide » le joue sur son écran. On ne
   propose pas un geste dans un module auquel on n'a pas accès.
   Un guide terminé le dit en haut à droite de sa carte (2026-10-09),
   « Terminé » et la coche ronde du panneau Savoirs, et l'on filtre ce qui
   reste à parcourir. Au clavier, Tab passe d'une carte à l'autre, Entrée
   lance le guide, ← et → le lisent. Le `tabIndex` explicite compte : sans
   lui, Safari ne pose pas le focus sur un bouton à la touche Tab. */
export default function WelcomeGuidesPage({ visibleModules = [], setPage, onOpenPanel }) {
  const { t } = useTranslation();
  const h = (k, o) => t(`cockpit:home.${k}`, o);
  const guides = useMemo(() => guidesFor(visibleModules), [visibleModules]);
  const [filter, setFilter] = useState('all');
  const [state, setState] = useState('all');
  const done = useProgress();

  const cards = useMemo(() => guides
    .filter((gd) => filter === 'all' || gd.id === filter)
    .flatMap((gd) => gd.topics.map((tp) => ({ ...(TOPICS.find((x) => x.id === tp.id) || tp), guide: gd })))
    .filter((tp) => state === 'all' || (state === 'done') === !!done[tp.id]),
  [guides, filter, state, done]);

  return (
    <PageShell title={t('nav:welcomeGuides')} actions={<BackHome setPage={setPage} />}>
      <div className="wch">
        {guides.length === 0 && <p className="wch-empty">{h('noGuides')}</p>}
        {guides.length > 0 && (
          <div className="wch-filter">
            {guides.length > 1 && (
              <PillFilter multiple={false} value={filter} onChange={setFilter}
                          options={[{ value: 'all', label: h('all') },
                            ...guides.map((gd) => ({ value: gd.id, label: gd.label }))]} />
            )}
            <SegmentedToggle value={state} onChange={setState}
                             options={[{ value: 'all', label: h('all') },
                               { value: 'todo', label: h('guidesTodo') },
                               { value: 'done', label: h('guidesDone') }]} />
          </div>
        )}
        <div className="wch-guides">
          {cards.map((tp) => (
            <button type="button" key={tp.id} className="wch-guide" tabIndex={0}
                    onClick={() => playTopic(tp, { setPage, openPanel: onOpenPanel })}>
              <span className="wch-guide__meta">
                {tp.guide.label}
                {done[tp.id] && (
                  <span className="wch-guide__done">
                    {h('guideDone')}
                    <span className="wch-check" aria-hidden="true"><Check size={10} strokeWidth={3} /></span>
                  </span>
                )}
              </span>
              <h3>{tp.title}</h3>
              {tp.summary && <p>{tp.summary}</p>}
              <b className="wch-guide__go">{h('readGuide')} <ArrowRight size={13} strokeWidth={1.8} aria-hidden="true" /></b>
            </button>
          ))}
        </div>
        {guides.length > 0 && !cards.length && <p className="wch-empty">{h('noGuidesHere')}</p>}
      </div>
    </PageShell>
  );
}
